import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, X } from 'lucide-react';
import { getServiceLineGross, getOfferTotals, getOfferSalePercent, formatSalePercent, shouldShowLinePrices, shouldShowSubtotals, shouldShowTotals, formatOfferTotalText } from '../utils/offerLineDisplay';
import { offerColorStyles, brandButtonProps } from '../utils/offerColor';
import useOfferEngagement from '../hooks/useOfferEngagement';

/**
 * Offer overview step: fetches and displays offer summary only.
 * Used in StepperChatbotWidget as a separate step before the contact form.
 */
const OfferOverviewStep = ({
  offerId,
  token,
  onContinue,
  onReject,
  compact = false,
  showRejectButton = true,
  showBackToChat = false,
  onBackToChat,
  previewOffer = null,
}) => {
  const { t } = useTranslation();
  const isPreview = previewOffer != null;
  const [offer, setOffer] = useState(previewOffer);
  const [isLoading, setIsLoading] = useState(!isPreview);
  const [error, setError] = useState(null);
  const trackEngagement = Boolean(!isPreview && offer && offer.status === null && !isLoading && !error);
  const readSentinelRef = useOfferEngagement({ offerId, token, active: trackEngagement });

  useEffect(() => {
    if (isPreview || !offerId || !token) return;
    fetchOffer();
  }, [offerId, token, isPreview]);

  const fetchOffer = async () => {
    try {
      if (!token) {
        setError(t('offer.invalidToken'));
        setIsLoading(false);
        return;
      }

      const response = await fetch(`/api/offers/${offerId}?token=${token}`);
      const data = await response.json();

      if (response.ok) {
        setOffer(data.offer);
      } else {
        if (response.status === 403) {
          setError(t('offer.invalidToken'));
        } else if (response.status === 410) {
          setError(t('offer.linkExpired'));
        } else {
          setError(data.error || t('offer.offerNotFound'));
        }
      }
    } catch (err) {
      console.error('Error fetching offer:', err);
      setError(t('offer.failedToLoadOffer'));
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center ${compact ? 'py-8' : 'min-h-[200px]'}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('offer.loadingOffer')}</p>
        </div>
      </div>
    );
  }

  if (error || !offer) {
    return (
      <div className={`flex items-center justify-center p-4 ${compact ? '' : 'min-h-[200px]'}`}>
        <div className="bg-white rounded-lg p-6 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <X className="h-8 w-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">{t('common.error')}</h2>
          <p className="text-gray-600 mb-6">{error || t('offer.offerNotFound')}</p>
        </div>
      </div>
    );
  }

  if (!isPreview && offer.status !== null) {
    return (
      <div className={`flex items-center justify-center p-4 ${compact ? '' : 'min-h-[200px]'}`}>
        <div className="bg-white rounded-lg p-6 text-center">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="h-8 w-8 text-yellow-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">{t('offer.offerAlreadyProcessed')}</h2>
          <p className="text-gray-600 mb-6">
            {t('offer.offerAlreadyProcessedMessage', { status: offer.status === 'pending' ? t('customer.status.accepted') : offer.status })}
          </p>
        </div>
      </div>
    );
  }

  const { subtotalNet, totalVat, totalGross } = getOfferTotals(offer);
  const salePercent = getOfferSalePercent(offer);
  const totalAmount = formatOfferTotalText(offer) || 'TBD';
  const showLinePrices = shouldShowLinePrices(offer);
  const showTotals = shouldShowTotals(offer);
  const showSubtotals = shouldShowSubtotals(offer);

  const containerClass = compact ? 'p-4' : 'p-6';
  const theme = offerColorStyles(offer.primaryColor);

  return (
    <div className={`${containerClass} overflow-y-auto max-h-[700px]`}>
      <h2 className={`font-bold text-gray-900 flex items-center mb-4 ${compact ? 'text-xl' : 'text-2xl'}`}>
        <FileText className="h-6 w-6 mr-2" style={{ color: theme.color }} />
        {t('offer.acceptYourOffer')}
      </h2>
      <p className="text-sm text-gray-600 mb-4">{t('offer.dataUsageNotice')}</p>

      <div className="border rounded-lg p-4 mb-6" style={theme.card}>
        {offer.services && offer.services.length > 0 ? (
          <>
            <div className="space-y-2">
              {offer.services.map((service, index) => (
                <div key={index} className="bg-white rounded p-3 text-sm flex justify-between items-start gap-4">
                  <div>
                    <div className="font-medium" style={theme.strong}>{service.name}</div>
                    {service.description && (
                      <div className="text-xs mt-1" style={theme.body}>{service.description}</div>
                    )}
                  </div>
                  {showLinePrices && service.price !== undefined && (
                    <div className="font-semibold whitespace-nowrap text-right" style={theme.strong}>
                      <div>{offer.currency || 'EUR'} {getServiceLineGross(service).toFixed(2)}</div>
                      {service.lineNet != null && service.lineVat > 0 && (
                        <div className="text-xs font-normal" style={theme.muted}>
                          {t('offer.lineNetVat', {
                            net: service.lineNet.toFixed(2),
                            vat: service.lineVat.toFixed(2)
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
            {showTotals && totalAmount !== 'TBD' && (
              <div className="mt-4 pt-3 border-t space-y-1" style={theme.divider}>
                {showSubtotals && subtotalNet != null && totalVat != null && totalVat > 0 && (
                  <>
                    <div className="flex justify-between items-center text-sm" style={theme.body}>
                      <span>{t('offer.subtotalNet')}</span>
                      <span>{offer.currency || 'EUR'} {Number(subtotalNet).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm" style={theme.body}>
                      <span>{t('offer.totalVat')}</span>
                      <span>{offer.currency || 'EUR'} {Number(totalVat).toFixed(2)}</span>
                    </div>
                  </>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-sm font-semibold" style={theme.strong}>
                    {offer.displayMode === 'range'
                      ? t('offer.estimatedTotal')
                      : totalVat > 0
                        ? t('offer.totalGross')
                        : t('offer.totalAmount')}
                  </span>
                  <span className="text-lg font-bold" style={theme.strong}>{totalAmount}</span>
                </div>
                {salePercent > 0 && (
                  <div className="text-sm font-medium" style={theme.strong}>
                    {t('offer.saleApplied', { percent: formatSalePercent(salePercent) })}
                  </div>
                )}
                {offer.validUntil && (
                  <div className="text-xs mt-1" style={theme.body}>
                    {t('offer.validUntil', { date: new Date(offer.validUntil).toLocaleDateString() })}
                  </div>
                )}
              </div>
            )}
            {!showTotals && salePercent > 0 && (
              <div className="mt-4 text-sm font-medium" style={theme.strong}>
                {t('offer.saleApplied', { percent: formatSalePercent(salePercent) })}
              </div>
            )}
            {offer.notes && (
              <div className="mt-4 pt-3 border-t" style={theme.divider}>
                <div className="text-xs italic whitespace-pre-wrap" style={theme.body}>{offer.notes}</div>
              </div>
            )}
          </>
        ) : (
          <p className="text-sm" style={theme.body}>{t('offer.noServices')}</p>
        )}
        <div ref={readSentinelRef} className="h-px w-full" aria-hidden="true" />
      </div>

      {!isPreview && (
      <div className="flex justify-end gap-3 flex-wrap">
        {showBackToChat && onBackToChat && (
          <button
            type="button"
            onClick={onBackToChat}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            {t('offer.developerBackToChat')}
          </button>
        )}
        {showRejectButton && onReject && (
          <button
            onClick={onReject}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center"
          >
            <X className="h-4 w-4 mr-2" />
            {t('offer.rejectOffer')}
          </button>
        )}
        <button
          onClick={onContinue}
          className="px-6 py-2 text-white rounded-md font-medium flex items-center"
          {...brandButtonProps(theme)}
        >
          {t('stepper.continueToContact')}
        </button>
      </div>
      )}
    </div>
  );
};

export default OfferOverviewStep;
